import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { fmtErr, API } from "@/shared/lib/api";
import { PageHeader, Btn, Input, Field, Select } from "@/shared/components/UI";
import { ArrowLeft, Download, FileText, Plus, X, Pencil } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  part_paid: "bg-blue-50 text-blue-700 border-blue-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  overdue: "bg-rose-50 text-rose-700 border-rose-200",
};

/**
 * Invoices — read-only listing of every Tally invoice uploaded across all
 * projects. New invoices are uploaded ONLY from the Project Billing flow
 * (/accounts/projects → drill-down → Upload Tally Invoice). This page is
 * the receivables follow-up surface: see balance, age, project link, PDF.
 */
export default function Invoices() {
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");

  function load() {
    const p = filterStatus ? { status: filterStatus } : {};
    api.get("/invoices", { params: p }).then((r) => setItems(r.data));
  }
  useEffect(load, [filterStatus]);

  const filtered = items.filter((i) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (
      (i.invoice_number || "").toLowerCase().includes(s) ||
      (i.upr_no || "").toLowerCase().includes(s) ||
      (i.project_code || "").toLowerCase().includes(s) ||
      (i.client_name || "").toLowerCase().includes(s)
    );
  });

  const totals = items.reduce((acc, i) => ({
    total: acc.total + (i.total || 0),
    received: acc.received + (i.amount_received || 0),
    balance: acc.balance + (i.balance || 0),
  }), { total: 0, received: 0, balance: 0 });

  return (
    <div className="p-6 lg:p-10" data-testid="invoices-page">
      <PageHeader kicker="Accounts / Receivables" title="All Invoices">
        <a href={`${API}/export/invoices`}
           className="h-11 px-4 inline-flex items-center gap-2 border border-zinc-300 hover:bg-zinc-100 text-sm rounded-full"
           data-testid="export-invoices">
          <Download className="w-4 h-4" /> Excel
        </a>
      </PageHeader>

      <div className="text-sm text-zinc-500 mt-1 mb-4 max-w-2xl">
        Invoices are uploaded from <Link to="/accounts/projects" className="text-orange-600 hover:underline">Project Billing</Link> after generating them in Tally.
        This page is for receivables follow-up.
      </div>

      {/* Roll-up KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Kpi label="Invoices" value={items.length} />
        <Kpi label="Billed" value={`₹${totals.total.toLocaleString("en-IN")}`} tone="zinc" />
        <Kpi label="Received" value={`₹${totals.received.toLocaleString("en-IN")}`} tone="emerald" />
        <Kpi label="Outstanding" value={`₹${totals.balance.toLocaleString("en-IN")}`} tone="rose" />
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder="Search invoice / project / client…" className="max-w-sm" />
        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="max-w-xs">
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="part_paid">Part Paid</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
        </Select>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-3">Invoice</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Client</th>
              <th className="text-left p-3">UPR / Project</th>
              <th className="text-right p-3">Total</th>
              <th className="text-right p-3">Received</th>
              <th className="text-right p-3">Balance</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Due</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="10" className="p-8 text-center text-zinc-400">No invoices uploaded yet.</td></tr>
            )}
            {filtered.map((i) => (
              <tr key={i.id} className="border-t border-zinc-100 hover:bg-orange-50/30 transition-colors">
                <td className="p-3 font-mono">
                  <Link to={`/invoices/${i.id}`} className="text-blue-700 hover:underline" data-testid={`invoice-link-${i.invoice_number}`}>
                    {i.invoice_number}
                  </Link>
                </td>
                <td className="p-3 font-mono text-xs">{i.invoice_date}</td>
                <td className="p-3 text-sm">{i.client_name || "—"}</td>
                <td className="p-3 font-mono text-xs">
                  {i.project_code ? (
                    <Link to={`/accounts/projects/${i.project_code}`} className="text-zinc-700 hover:text-orange-600" data-testid={`invoice-upr-${i.id}`}>
                      {i.upr_no ? (
                        <>
                          <span className="font-semibold text-emerald-700">{i.upr_no}</span>
                          <span className="block text-[10px] text-zinc-400"><ProjRef code={i.project_code} /></span>
                        </>
                      ) : (
                        <>
                          {i.project_code}
                          <span className="block text-[10px] text-amber-600">UPR pending</span>
                        </>
                      )}
                    </Link>
                  ) : "—"}
                </td>
                <td className="p-3 text-right font-mono">₹{(i.total || 0).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono">₹{(i.amount_received || 0).toLocaleString("en-IN")}</td>
                <td className="p-3 text-right font-mono font-semibold">
                  {i.balance > 0
                    ? <span className="text-rose-600">₹{i.balance.toLocaleString("en-IN")}</span>
                    : <span className="text-emerald-600">cleared</span>}
                </td>
                <td className="p-3">
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded-full border ${STATUS_COLOR[i.payment_status] || STATUS_COLOR.pending}`}>
                    {i.payment_status}
                  </span>
                </td>
                <td className="p-3 font-mono text-xs">{i.due_date || "—"}</td>
                <td className="p-3 text-right">
                  {i.tally_pdf_url && (
                    <a href={i.tally_pdf_url} target="_blank" rel="noopener noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline">
                      <FileText className="w-3 h-3" /> PDF
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const tones = {
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    rose:    "text-rose-700 bg-rose-50 border-rose-100",
    zinc:    "text-zinc-800 bg-zinc-50 border-zinc-100",
  };
  return (
    <div className={`rounded-2xl p-4 border ${tones[tone] || "text-zinc-700 bg-white border-zinc-100"}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}

export function InvoiceDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [pf, setPf] = useState({ amount: 0, payment_date: "", mode: "bank", reference: "", tds: 0, remarks: "" });

  function load() { api.get(`/invoices/${id}`).then((r) => setData(r.data)); }
  useEffect(load, [id]);

  async function recordPayment(e) {
    e.preventDefault();
    try {
      await api.post("/payments", { invoice_id: data.invoice.id, ...pf, amount: Number(pf.amount), tds: Number(pf.tds) });
      toast.success("Payment recorded"); setOpen(false); load();
      setPf({ amount: 0, payment_date: "", mode: "bank", reference: "", tds: 0, remarks: "" });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function delPay(pid) {
    if (!confirm("Delete payment?")) return;
    await api.delete(`/payments/${pid}`); load();
  }
  const [editNo, setEditNo] = useState(false);
  const [noForm, setNoForm] = useState({ invoice_number: "", note: "" });
  async function saveNumber(e) {
    e.preventDefault();
    try {
      await api.patch(`/invoices/${inv.id}/number`, noForm);
      toast.success("Invoice number corrected");
      setEditNo(false); setNoForm({ invoice_number: "", note: "" }); load();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
  }

  if (!data) return <div className="p-8 text-zinc-500">Loading…</div>;
  const inv = data.invoice;

  return (
    <div className="p-6 lg:p-10" data-testid="invoice-detail">
      <Link to="/invoices" className="inline-flex items-center gap-2 text-sm text-zinc-600 mb-4"><ArrowLeft className="w-4 h-4" /> Back</Link>
      <PageHeader kicker={inv.invoice_number} title={`${inv.client_name} · ${inv.milestone.toUpperCase()}`}>
        <button onClick={() => setEditNo(true)} title="Correct invoice number"
                className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-orange-600 border border-zinc-300 hover:border-orange-400 rounded-md px-2.5 py-1.5 transition-colors"
                data-testid="edit-invoice-number-btn">
          <Pencil className="w-3.5 h-3.5" /> Edit number
        </button>
        <span className={`text-xs font-mono uppercase px-2 py-1 border ${STATUS_COLOR[inv.payment_status]}`}>{inv.payment_status}</span>
        {inv.balance > 0 && <Btn onClick={() => setOpen(true)} data-testid="record-payment-btn"><Plus className="w-4 h-4" /> Record Payment</Btn>}
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <SmallStat label="Total" value={`₹${inv.total.toLocaleString()}`} />
        <SmallStat label="Tax" value={`₹${inv.tax_amount.toLocaleString()}`} />
        <SmallStat label="Received" value={`₹${inv.amount_received.toLocaleString()}`} accent="text-emerald-600" />
        <SmallStat label="Balance" value={`₹${inv.balance.toLocaleString()}`} accent="text-rose-600" />
      </div>

      <div className="card-flat p-5 mb-4 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
        <KV k="UPR No." v={inv.upr_no ? <span className="font-mono font-semibold text-emerald-700">{inv.upr_no}</span> : <span className="text-amber-600">UPR pending</span>} />
        <KV k="Project" v={inv.project_code ? <Link to={`/projects/${inv.project_code}`} className="text-blue-600">{inv.project_code}</Link> : "—"} />
        <KV k="Client PO" v={inv.client_po_number || "—"} />
        <KV k="Invoice Date" v={inv.invoice_date} mono />
        <KV k="Due Date" v={inv.due_date || "—"} mono />
        {inv.copy_url && <KV k="Copy" v={<a href={inv.copy_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">Open</a>} />}
        {inv.remarks && <KV k="Remarks" v={inv.remarks} />}
        {(inv.number_change_log || []).length > 0 && (
          <KV k="Number corrections" v={
            <div className="space-y-1" data-testid="number-change-log">
              {inv.number_change_log.map((l, i) => (
                <div key={i} className="text-xs">
                  <span className="font-mono">{l.from} → {l.to}</span>
                  <span className="text-zinc-400"> · {l.by} · {(l.at || "").slice(0, 10)}</span>
                  <div className="text-zinc-500 italic">"{l.note}"</div>
                </div>
              ))}
            </div>
          } />
        )}
      </div>

      <div className="card-flat overflow-x-auto">
        <div className="px-5 py-4 border-b border-zinc-200"><h3 className="font-display font-bold tracking-tight">Payments</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Date</th><th className="text-right p-3">Amount</th><th className="text-right p-3">TDS</th><th className="text-left p-3">Mode</th><th className="text-left p-3">Reference / UTR</th><th className="text-left p-3">By</th><th></th></tr>
          </thead>
          <tbody>
            {data.payments.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-zinc-400">No payments yet</td></tr>}
            {data.payments.map((p) => (
              <tr key={p.id} className="border-t border-zinc-100">
                <td className="p-3 font-mono text-xs">{p.payment_date}</td>
                <td className="p-3 text-right font-mono">₹{p.amount.toLocaleString()}</td>
                <td className="p-3 text-right font-mono">₹{(p.tds || 0).toLocaleString()}</td>
                <td className="p-3 uppercase text-xs">{p.mode}</td>
                <td className="p-3 font-mono text-xs">{p.reference}</td>
                <td className="p-3 text-xs">{p.created_by}</td>
                <td className="p-3 text-right"><button onClick={() => delPay(p.id)} className="text-rose-600 text-xs">Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editNo && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={saveNumber} className="bg-white max-w-md w-full p-6 space-y-3 border border-zinc-200" data-testid="edit-number-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">Correct Invoice Number</h3>
              <button type="button" onClick={() => setEditNo(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs font-mono text-zinc-500">Current: {inv.invoice_number}</div>
            <Field label="Corrected invoice number" required>
              <Input value={noForm.invoice_number} onChange={(e) => setNoForm({ ...noForm, invoice_number: e.target.value })}
                     required data-testid="edit-number-input" />
            </Field>
            <Field label="Reason for correction (recorded in audit)" required>
              <Input value={noForm.note} onChange={(e) => setNoForm({ ...noForm, note: e.target.value })}
                     placeholder="e.g. Typo — Tally shows 26-27/0042" required data-testid="edit-number-note" />
            </Field>
            <div className="text-[11px] text-zinc-400">Payments and delivery challans linked to this invoice are updated automatically.</div>
            <div className="flex gap-2">
              <Btn type="submit" data-testid="save-number-btn">Save Correction</Btn>
              <Btn type="button" variant="secondary" onClick={() => setEditNo(false)}>Cancel</Btn>
            </div>
          </form>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={recordPayment} className="bg-white max-w-md w-full p-6 space-y-3 border border-zinc-200">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">Record Payment</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs font-mono text-zinc-500">
              {inv.invoice_number}
              {inv.upr_no && <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold" data-testid="rp-upr-chip">UPR {inv.upr_no}</span>}
            </div>
            <Field label="Amount Received (₹)" required><Input type="number" min="0.01" step="0.01" max={inv.balance} value={pf.amount} onChange={(e) => setPf({ ...pf, amount: e.target.value })} required data-testid="payment-amount-input" /></Field>
            <Field label="TDS (₹)"><Input type="number" min="0" step="0.01" value={pf.tds} onChange={(e) => setPf({ ...pf, tds: e.target.value })} /></Field>
            <Field label="Payment Date"><Input type="date" value={pf.payment_date} onChange={(e) => setPf({ ...pf, payment_date: e.target.value })} /></Field>
            <Field label="Mode">
              <Select value={pf.mode} onChange={(e) => setPf({ ...pf, mode: e.target.value })}>
                <option value="bank">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
              </Select>
            </Field>
            <Field label="UTR / Reference"><Input value={pf.reference} onChange={(e) => setPf({ ...pf, reference: e.target.value })} /></Field>
            <Field label="Remarks"><Input value={pf.remarks} onChange={(e) => setPf({ ...pf, remarks: e.target.value })} /></Field>
            <div className="flex gap-2"><Btn type="submit" data-testid="save-payment-btn">Record</Btn><Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn></div>
          </form>
        </div>
      )}
    </div>
  );
}

function SmallStat({ label, value, accent }) {
  return (
    <div className="card-flat p-4">
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${accent || ""}`}>{value}</div>
    </div>
  );
}
function KV({ k, v }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{k}</div>
      <div className="mt-0.5 text-sm">{v || "—"}</div>
    </div>
  );
}
